import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import { ShoppingCart, Loader2 } from 'lucide-react';
import { PartNoCell } from '../shared/PartNoCell';

/**
 * "Create Sales Order" dialog extracted from QuotationDetailPage.
 * Pure presentational — all state and mutations are owned by the parent
 * (`QuotationDetailPage`) and passed in as props. No behavioural change.
 */
export function CreateSalesOrderDialog({
  open, onOpenChange, quotation,
  customerPO, setCustomerPO,
  customerPODate, setCustomerPODate,
  orderTaxPercentage, setOrderTaxPercentage,
  shipToAddress, setShipToAddress,
  orderItems, updateOrderItem,
  selectedItems, toggleItemSelection, toggleAllItems,
  formatCurrency,
  calculateOrderSubtotal, calculateOrderTaxAmount, calculateOrderGrandTotal,
  calculateOrderChargesBase,
  handleCreateOrder, creatingOrder,
}) {
  const charges = calculateOrderChargesBase ? calculateOrderChargesBase() : { tooling: 0, sample: 0, handling: 0 };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Create Sales Order
          </DialogTitle>
          <DialogDescription>
            Convert quotation {quotation.quotation_number} into a Sales Order. You can adjust quantity and price before creating.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* Customer PO Information */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Customer PO Number</Label>
              <Input
                value={customerPO}
                onChange={(e) => setCustomerPO(e.target.value)}
                placeholder="e.g., PO-2026-001"
                data-testid="order-customer-po"
              />
            </div>
            <div className="space-y-2">
              <Label>Customer PO Date</Label>
              <Input
                type="date"
                value={customerPODate}
                onChange={(e) => setCustomerPODate(e.target.value)}
                data-testid="order-customer-po-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Tax / GST (%)</Label>
              <Input
                type="number"
                step="0.01"
                value={orderTaxPercentage}
                onChange={(e) => setOrderTaxPercentage(parseFloat(e.target.value) || 0)}
                placeholder="18"
                data-testid="order-tax-percentage"
              />
            </div>
          </div>

          {/* Ship To Address */}
          <div className="space-y-2">
            <Label>Ship To Address</Label>
            <Textarea
              value={shipToAddress}
              onChange={(e) => setShipToAddress(e.target.value)}
              placeholder="Delivery / shipping address (leave blank to use customer billing address)"
              rows={3}
              data-testid="order-ship-to-address"
            />
          </div>

          {/* Select Line Items - Editable */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label>Select Items to Include (Quantity and Price are Editable)</Label>
              <Button variant="ghost" size="sm" onClick={toggleAllItems}>
                {selectedItems.length === quotation.items?.length ? 'Deselect All' : 'Select All'}
              </Button>
            </div>
            <div className="border rounded-lg overflow-x-auto">
              <div style={{ minWidth: '800px' }}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">Include</TableHead>
                      <TableHead style={{ minWidth: '140px' }}>Part No</TableHead>
                      <TableHead style={{ minWidth: '180px' }}>Description</TableHead>
                      <TableHead style={{ minWidth: '100px' }}>Qty</TableHead>
                      <TableHead style={{ minWidth: '120px' }}>Unit Price</TableHead>
                      <TableHead className="text-right" style={{ minWidth: '100px' }}>Line Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {orderItems.map((item) => {
                      const isSelected = selectedItems.includes(item.id);
                      const lineTotal = ((parseFloat(item.edited_quantity) || 0) * (parseFloat(item.edited_unit_price) || 0)) / (item.price_unit || 1);
                      return (
                        <TableRow
                          key={item.id}
                          className={`${isSelected ? 'bg-green-50' : 'opacity-60'}`}
                        >
                          <TableCell>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleItemSelection(item.id)}
                              className="h-4 w-4 rounded border-gray-300"
                              data-testid={`order-item-checkbox-${item.id}`}
                            />
                          </TableCell>
                          <TableCell><PartNoCell customerPartNo={item.customer_part_no} internalPartNo={item.part_internal_no} /></TableCell>
                          <TableCell className="text-sm">{item.description}</TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              value={item.edited_quantity}
                              onChange={(e) => updateOrderItem(item.id, 'edited_quantity', e.target.value)}
                              className="h-8 text-sm font-mono"
                              style={{ minWidth: '80px' }}
                              disabled={!isSelected}
                              data-testid={`order-item-qty-${item.id}`}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number"
                              step="0.00001"
                              value={item.edited_unit_price}
                              onChange={(e) => updateOrderItem(item.id, 'edited_unit_price', e.target.value)}
                              className="h-8 text-sm font-mono"
                              style={{ minWidth: '100px' }}
                              disabled={!isSelected}
                              data-testid={`order-item-price-${item.id}`}
                            />
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {formatCurrency(lineTotal)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
            <p className="text-sm text-muted-foreground">
              {selectedItems.length} of {quotation.items?.length} items selected
            </p>
          </div>

          {/* Order Totals with Tax */}
          {selectedItems.length > 0 && (
            <div className="pt-4 border-t">
              <div className="flex justify-end">
                <div className="w-72 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Subtotal:</span>
                    <span className="font-mono">{formatCurrency(calculateOrderSubtotal())}</span>
                  </div>
                  {charges.tooling > 0 && (
                    <div className="flex justify-between text-sm" data-testid="order-preview-tooling">
                      <span className="text-muted-foreground">Tooling Cost:</span>
                      <span className="font-mono">{formatCurrency(charges.tooling)}</span>
                    </div>
                  )}
                  {charges.sample > 0 && (
                    <div className="flex justify-between text-sm" data-testid="order-preview-sample">
                      <span className="text-muted-foreground">Sample Cost:</span>
                      <span className="font-mono">{formatCurrency(charges.sample)}</span>
                    </div>
                  )}
                  {charges.handling > 0 && (
                    <div className="flex justify-between text-sm" data-testid="order-preview-handling">
                      <span className="text-muted-foreground">Handling Charge:</span>
                      <span className="font-mono">{formatCurrency(charges.handling)}</span>
                    </div>
                  )}
                  {(parseFloat(orderTaxPercentage) || 0) > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Tax ({orderTaxPercentage}%):</span>
                      <span className="font-mono">{formatCurrency(calculateOrderTaxAmount())}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg border-t pt-2">
                    <span>Grand Total:</span>
                    <span className="font-mono text-primary" data-testid="order-preview-grand-total">{formatCurrency(calculateOrderGrandTotal())}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            className="btn-primary"
            onClick={handleCreateOrder}
            disabled={creatingOrder || selectedItems.length === 0}
            data-testid="create-sales-order-btn"
          >
            {creatingOrder ? (
              <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating...</>
            ) : (
              <><ShoppingCart className="mr-2 h-4 w-4" />Create Order</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
